<template>
  <div>
    <Head title="Evaluari" />
    <div class="flex items-center justify-between mb-8">
      <h1 class="text-3xl font-bold">Evaluari</h1>
      <button class="btn-teal" @click="showAllocateDialog = true">
        <i class="pi pi-plus mr-1"></i> Aloca examen
      </button>
    </div>

    <!-- Tabs -->
    <div class="flex border-b mb-4">
      <button
        class="px-6 py-3 font-medium transition-colors"
        :class="
          activeTab === 'results'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        "
        @click="activeTab = 'results'"
      >
        Rezultate
      </button>
      <button
        class="px-6 py-3 font-medium transition-colors"
        :class="
          activeTab === 'scheduled'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        "
        @click="activeTab = 'scheduled'"
      >
        Programate
      </button>
    </div>

    <!-- Results tab -->
    <div
      v-if="activeTab === 'results'"
      class="overflow-x-auto rounded-sm bg-white shadow-sm"
    >
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="px-6 pb-4 pt-6">Utilizator</th>
            <th class="px-6 pb-4 pt-6">Curs</th>
            <th class="px-6 pb-4 pt-6">Quiz</th>
            <th class="px-6 pb-4 pt-6">Tip</th>
            <th v-tooltip.top="'Batch'" class="px-6 pb-4 pt-6">B#</th>
            <th class="px-6 pb-4 pt-6">
              <i class="pi pi-stopwatch"></i>
            </th>
            <th class="px-6 pb-4 pt-6">Rezultat</th>
            <th class="px-6 pb-4 pt-6">Data</th>
            <th class="px-3 pb-4 pt-6"></th>
            <th class="px-3 pb-4 pt-6"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="result in completedResults"
            :key="result.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100"
          >
            <td class="border-t px-6 py-4">
              {{ result.user_name }}
            </td>
            <td
              class="border-t px-6 py-4 max-w-60 truncate"
              :title="result.course_name"
            >
              {{ result.course_name }}
            </td>
            <td class="border-t px-6 py-4">
              {{ result.subject_name }}
            </td>
            <td class="border-t px-6 py-4">
              <span
                class="px-2 py-1 rounded text-sm"
                :class="quizTypeBadgeClass(result.quiz_type)"
              >
                {{ quizTypeLabel(result.quiz_type) }}
              </span>
            </td>
            <td class="border-t px-6 py-4">
              {{ result.batch_number || '-' }}
            </td>
            <td class="border-t px-6 py-4 text-gray-600">
              {{
                result.quiz_type == 'exam' && result.duration
                  ? result.duration + 'm'
                  : '-'
              }}
            </td>
            <td class="border-t px-6 py-4">
              <div class="flex flex-row">
                <span
                  class="text-md"
                  :class="
                    quizResultPassed(
                      result.correct_count,
                      result.total_questions,
                    )
                      ? 'text-green-600'
                      : 'text-red-600'
                  "
                >
                  {{
                    quizResultPercentage(
                      result.correct_count,
                      result.total_questions,
                    )
                  }}%
                </span>
                <span class="text-md text-gray-500">
                  &nbsp;({{ result.correct_count }}/{{
                    result.total_questions
                  }})
                </span>
              </div>
            </td>
            <td class="border-t px-6 py-4 text-sm text-gray-600">
              {{ formatDate(result.updated_at) }}
            </td>
            <td class="border-t px-6 py-4 text-center">
              <a
                v-if="result.quiz_type === 'exam'"
                :href="$routes.export_csv_quiz_result(result.id)"
                class="text-blue-600 hover:text-blue-800 text-sm"
                target="_blank"
              >
                <i class="pi pi-download"></i>
              </a>
            </td>
            <td class="border-t px-6 py-4 text-center">
              <button
                class="text-red-600 hover:text-red-800 cursor-pointer"
                @click="confirmDelete(result)"
              >
                <i class="pi pi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="completedResults.length === 0">
            <td class="border-t px-6 py-4" colspan="8">Nu sunt rezultate.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Scheduled tab -->
    <div
      v-if="activeTab === 'scheduled'"
      class="overflow-x-auto rounded-sm bg-white shadow-sm"
    >
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="px-6 pb-4 pt-6">Utilizator</th>
            <th class="px-6 pb-4 pt-6">Curs</th>
            <th v-tooltip.top="'Batch'" class="px-6 pb-4 pt-6">B#</th>
            <th class="px-6 pb-4 pt-6">Intr.</th>
            <th class="px-6 pb-4 pt-6">Status</th>
            <th class="px-6 pb-4 pt-6">
              <i class="pi pi-stopwatch"></i>
            </th>
            <th class="px-6 pb-4 pt-6">Sfarsit</th>
            <th class="px-6 pb-4 pt-6"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="result in scheduledResults"
            :key="result.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100"
          >
            <td class="border-t px-6 py-4">
              {{ result.user_name }}
            </td>
            <td
              class="border-t px-6 py-4 max-w-60 truncate"
              :title="result.course_name"
            >
              {{ result.course_name }}
            </td>
            <td class="border-t px-6 py-4">
              {{ result.batch_number || '-' }}
            </td>
            <td class="border-t px-6 py-4">
              {{ result.questions_in_batch ?? '-' }}
            </td>
            <td class="border-t px-6 py-4">
              <span
                class="px-2 py-1 rounded text-sm"
                :class="
                  result.started
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                "
              >
                {{ result.started ? 'In desfasurare' : 'Neinceput' }}
              </span>
            </td>
            <td class="border-t px-6 py-4 text-sm text-gray-600">
              {{ result.duration ? result.duration + 'm' : '-' }}
            </td>
            <td class="border-t px-6 py-4 text-sm text-gray-600">
              {{ result.end_time ? formatDate(result.end_time) : '-' }}
            </td>
            <td
              class="border-t px-6 py-4 text-center flex items-center gap-2 justify-end"
            >
              <button
                v-if="result.started"
                class="btn btn-small border border-blue-400 text-blue-700 rounded-sm font-bold text-sm bg-white hover:bg-blue-50 cursor-pointer mr-6"
                :disabled="addingTime[result.id]"
                @click="addFiveMinutes(result)"
              >
                +5 min
              </button>
              <button
                class="text-red-600 hover:text-red-800 cursor-pointer"
                @click="confirmDelete(result)"
              >
                <i class="pi pi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="scheduledResults.length === 0">
            <td class="border-t px-6 py-4" colspan="7">
              Nu sunt examene programate.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Allocate Exam Dialog -->
    <Dialog
      v-model:visible="showAllocateDialog"
      modal
      :draggable="false"
      header="Aloca examen"
      class="w-100"
    >
      <div class="space-y-4">
        <!-- User selection -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Utilizator</label
          >
          <select
            v-model="allocateForm.user_id"
            class="form-select w-full border rounded px-3 py-2"
            @change="onAllocateUserChange"
          >
            <option value="">Selecteaza utilizator...</option>
            <option
              v-for="user in allocatableUsers"
              :key="user.id"
              :value="user.id"
            >
              {{ user.name }}
            </option>
          </select>
        </div>

        <!-- Course selection -->
        <div v-if="allocateForm.user_id">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Curs</label
          >
          <select
            v-model="allocateForm.course_id"
            class="form-select w-full border rounded px-3 py-2"
            @change="onAllocateCourseChange"
          >
            <option value="">Selecteaza curs...</option>
            <option
              v-for="course in allocatableCourses"
              :key="course.id"
              :value="course.id"
            >
              {{ course.name }}
            </option>
          </select>
        </div>

        <!-- Batch selection -->
        <div v-if="allocateForm.course_id && selectedCourseData">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Batch</label
          >
          <div class="flex gap-2">
            <button
              v-for="batch in selectedCourseData.batches"
              :key="batch"
              type="button"
              class="px-4 py-2 rounded border-2 font-medium transition-all"
              :class="
                allocateForm.batch_number === batch
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              "
              @click="allocateForm.batch_number = batch"
            >
              Batch {{ batch }}
            </button>
          </div>
        </div>

        <!-- Duration -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Durata (minute)</label
          >
          <input
            v-model.number="allocateForm.duration"
            type="number"
            min="1"
            class="form-input w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      <template #footer>
        <button
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium cursor-pointer"
          @click="closeAllocateDialog"
        >
          Anuleaza
        </button>
        <button
          :disabled="!canAllocate"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          @click="submitAllocate"
        >
          Aloca
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { Head, router } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import axios from 'axios';
import { quizResultPercentage, quizResultPassed } from '@/utils/formatters';
import Dialog from 'primevue/dialog';

export default {
  components: {
    Head,
    Dialog,
  },
  layout: Layout,
  props: {
    completedResults: {
      type: Array,
      required: true,
    },
    scheduledResults: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'results',
      showAllocateDialog: false,
      allocatableUsers: [],
      allocatableCourses: [],
      addingTime: {},
      allocateForm: {
        user_id: '',
        course_id: '',
        batch_number: null,
        duration: 120,
      },
    };
  },
  computed: {
    selectedCourseData() {
      if (!this.allocateForm.course_id) return null;
      return this.allocatableCourses.find(
        (c) => c.id === parseInt(this.allocateForm.course_id),
      );
    },
    canAllocate() {
      return (
        this.allocateForm.user_id &&
        this.allocateForm.course_id &&
        this.allocateForm.batch_number
      );
    },
  },
  watch: {
    showAllocateDialog(val) {
      if (val) {
        this.loadAllocatableUsers();
      }
    },
  },
  methods: {
    quizResultPercentage,
    quizResultPassed,
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    quizTypeLabel(quizType) {
      switch (quizType) {
        case 'course':
          return 'Curs';
        case 'practice':
          return 'Practica';
        case 'exam':
          return 'Examen';
        default:
          return quizType;
      }
    },
    quizTypeBadgeClass(quizType) {
      switch (quizType) {
        case 'course':
          return 'bg-blue-100 text-blue-800';
        case 'practice':
          return 'bg-green-100 text-green-800';
        case 'exam':
          return 'bg-purple-100 text-purple-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },
    confirmDelete(result) {
      const name = result.user_name;
      const subject = result.subject_name;
      if (
        confirm(
          `Esti sigur ca vrei sa stergi rezultatul utilizatorului ${name} pentru quiz-ul "${subject}"?`,
        )
      ) {
        this.deleteResult(result.id);
      }
    },
    deleteResult(id) {
      router.delete(this.$routes.quiz_result(id), {
        preserveScroll: true,
        onError: () => {
          alert('Eroare la stergerea rezultatului.');
        },
      });
    },
    async openAllocateDialog() {
      this.showAllocateDialog = true;
      await this.loadAllocatableUsers();
    },
    closeAllocateDialog() {
      this.showAllocateDialog = false;
      this.allocateForm = {
        user_id: '',
        course_id: '',
        batch_number: null,
        duration: 120,
      };
      this.allocatableCourses = [];
    },
    async loadAllocatableUsers() {
      try {
        const response = await axios.get(
          this.$routes.allocatable_users_quiz_results(),
        );
        this.allocatableUsers = response.data;
      } catch (error) {
        console.error('Error loading users:', error);
      }
    },
    async onAllocateUserChange() {
      this.allocateForm.course_id = '';
      this.allocateForm.batch_number = null;
      this.allocatableCourses = [];
      if (!this.allocateForm.user_id) return;
      try {
        const response = await axios.get(
          this.$routes.allocatable_courses_quiz_results(),
          {
            params: { user_id: this.allocateForm.user_id },
          },
        );
        this.allocatableCourses = response.data;
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    },
    onAllocateCourseChange() {
      this.allocateForm.batch_number = null;
    },
    submitAllocate() {
      if (!this.canAllocate) return;
      const courseData = this.selectedCourseData;
      router.post(
        this.$routes.allocate_exam_quiz_results(),
        {
          user_id: this.allocateForm.user_id,
          subject_id: courseData.exam_subject_id,
          batch_number: this.allocateForm.batch_number,
          duration: this.allocateForm.duration,
        },
        {
          onSuccess: () => {
            this.closeAllocateDialog();
          },
          onError: () => {
            alert('Eroare la alocarea examenului.');
          },
        },
      );
    },
    async addFiveMinutes(result) {
      this.addingTime = { ...this.addingTime, [result.id]: true };
      try {
        const response = await axios.patch(
          this.$routes.add_time_quiz_result(result.id),
        );
        result.end_time = response.data.end_time;
      } catch {
        alert('Eroare la adaugarea timpului.');
      } finally {
        this.addingTime = { ...this.addingTime, [result.id]: false };
      }
    },
  },
};
</script>
